import * as RadixCheckbox from '@radix-ui/react-checkbox'
import { Check, Loader2 } from 'lucide-react'
import type { ReactNode } from 'react'
import { cn } from '@/lib/utils'

export function Checkbox({
  checked,
  onCheckedChange,
  id,
  label,
  className,
}: {
  checked: boolean
  onCheckedChange: (checked: boolean) => void
  id?: string
  label?: ReactNode
  className?: string
}) {
  const control = (
    <RadixCheckbox.Root
      id={id}
      checked={checked}
      onCheckedChange={(state) => onCheckedChange(state === true)}
      className={cn(
        'flex size-4 shrink-0 items-center justify-center rounded border border-line-strong bg-paper',
        'data-[state=checked]:border-brand data-[state=checked]:bg-brand data-[state=checked]:text-white',
        className,
      )}
    >
      <RadixCheckbox.Indicator>
        <Check className="size-3" />
      </RadixCheckbox.Indicator>
    </RadixCheckbox.Root>
  )

  if (!label) return control

  return (
    <label className="flex cursor-pointer items-center gap-2 text-sm text-ink-soft">
      {control}
      {label}
    </label>
  )
}

export function Card({ className, children }: { className?: string; children: ReactNode }) {
  return (
    <div
      className={cn(
        'rounded-[var(--radius-card)] border border-line bg-paper shadow-[var(--shadow-card)]',
        className,
      )}
    >
      {children}
    </div>
  )
}

export function PageHeading({
  title,
  description,
  actions,
}: {
  title: string
  description?: string
  actions?: ReactNode
}) {
  return (
    <div className="mb-6 flex items-end justify-between gap-4">
      <div>
        <h1 className="font-display text-[28px] font-semibold leading-tight text-ink">{title}</h1>
        {description && <p className="mt-1 text-sm text-ink-muted">{description}</p>}
      </div>
      {actions && <div className="flex items-center gap-2">{actions}</div>}
    </div>
  )
}

export function Spinner({ className }: { className?: string }) {
  return <Loader2 className={cn('size-5 animate-spin text-ink-faint', className)} />
}

export function LoadingPanel({ label = 'Loading' }: { label?: string }) {
  return (
    <div className="flex items-center justify-center gap-2 py-16 text-sm text-ink-muted">
      <Spinner />
      {label}
    </div>
  )
}

export function EmptyState({
  title,
  description,
  action,
  icon,
}: {
  title: string
  description?: string
  action?: ReactNode
  icon?: ReactNode
}) {
  return (
    <div className="flex flex-col items-center justify-center gap-3 rounded-[var(--radius-card)] border border-dashed border-line bg-paper-muted/60 px-6 py-14 text-center">
      {icon && <div className="text-ink-faint">{icon}</div>}
      <div>
        <p className="font-display text-lg font-semibold text-ink">{title}</p>
        {description && <p className="mt-1 max-w-md text-sm text-ink-muted">{description}</p>}
      </div>
      {action}
    </div>
  )
}

/** The pale notice blocks used throughout the mockups. */
export function Notice({
  tone = 'neutral',
  title,
  children,
  icon,
}: {
  tone?: 'neutral' | 'caution' | 'brand' | 'positive'
  title?: ReactNode
  children?: ReactNode
  icon?: ReactNode
}) {
  const tones = {
    neutral: 'border-line bg-paper-muted text-ink-soft',
    caution: 'border-caution/25 bg-caution-soft text-caution',
    brand: 'border-brand-line bg-brand-soft text-brand',
    positive: 'border-positive/25 bg-positive-soft text-positive',
  }
  return (
    <div className={cn('rounded-[var(--radius-control)] border px-4 py-3 text-sm', tones[tone])}>
      {title && (
        <p className="flex items-center gap-1.5 font-semibold">
          {icon}
          {title}
        </p>
      )}
      {children && <div className={cn('text-[13px] leading-relaxed', title && 'mt-1')}>{children}</div>}
    </div>
  )
}
